/** @jsx jsx */
import React, { useCallback, type ReactNode } from 'react';
import { useRelayEnvironment } from 'react-relay/hooks';
import { Box } from '@atlaskit/primitives';
import { Inline, Text, Flex } from '@atlaskit/primitives/compiled';
import Button from '@atlaskit/button/new';
import { cssMap, jsx } from '@atlaskit/css';
import { token } from '@atlaskit/tokens';
import AddIcon from '@atlaskit/icon/core/add';
import InformationIcon from '@atlaskit/icon/core/status-information';
import Anchor from '@atlaskit/primitives/anchor';
import { fg } from '@atlassian/jira-feature-gating';
import { useIntl } from '@atlassian/jira-intl';
import { useIsAdmin } from '@atlassian/jira-tenant-context-controller/src/components/is-admin/index.tsx';
import {
	useIsSimplifiedProject,
	useProjectContext,
} from '@atlassian/jira-providers-project-context/src/index.tsx';
import {
	createOptimisticFieldConfig,
	deleteOptimisticFieldConfig,
} from '@atlassian/jira-issue-table-column-picker/src/ui/utils.tsx';
import type { FooterType } from '@atlassian/jira-issue-table-column-picker/src/common/types.tsx';

import { expVal } from '@atlassian/jira-feature-experiments';
import { fireUIAnalytics, useAnalyticsEvents } from '@atlassian/jira-product-analytics-bridge';
import { useInlineCreateActiveIndexActions } from '../../../../controllers/inline-create-active-index/index.tsx';
import { FIELD } from '../../../../controllers/inline-create-active-index/types.tsx';
import { useIFCConfigContext } from '../context/index.tsx';
import { useIFCEditField } from '../edit-field/index.tsx';
import messages from './messages.tsx';
import { TEMP_CREATE_FIELDSET_ID, updateTempOptimisticFieldSetId } from './utils.tsx';

const useIsProjectAdmin = () => {
	const { data } = useProjectContext();
	return !!data?.isProjectAdmin;
};

const styles = cssMap({
	ifcFooter: {
		// eslint-disable-next-line @atlaskit/design-system/no-physical-properties
		paddingTop: token('space.200'),
		// eslint-disable-next-line @atlaskit/design-system/no-physical-properties
		paddingRight: token('space.200'),
		// eslint-disable-next-line @atlaskit/design-system/no-physical-properties
		paddingBottom: token('space.200'),
		// eslint-disable-next-line @atlaskit/design-system/no-physical-properties
		paddingLeft: token('space.200'),
		display: 'flex',
		flexDirection: 'row',
		borderColor: token('color.border'),
		// eslint-disable-next-line @atlaskit/design-system/no-physical-properties
		borderTopStyle: 'solid',
		// eslint-disable-next-line @atlaskit/design-system/no-physical-properties
		borderTopWidth: token('border.width'),
		justifyContent: 'center',
	},
	footerContainer: {
		borderColor: token('color.border'),
		// eslint-disable-next-line @atlaskit/design-system/no-physical-properties
		borderTopStyle: 'solid',
		// eslint-disable-next-line @atlaskit/design-system/no-physical-properties
		borderTopWidth: token('border.width'),
		// eslint-disable-next-line @atlaskit/design-system/no-physical-properties
		paddingTop: token('space.150'),
		// eslint-disable-next-line @atlaskit/design-system/no-physical-properties
		paddingRight: token('space.150'),
		// eslint-disable-next-line @atlaskit/design-system/no-physical-properties
		paddingBottom: token('space.150'),
		// eslint-disable-next-line @atlaskit/design-system/no-physical-properties
		paddingLeft: token('space.150'),
	},
	anchor: {
		textDecoration: 'none',
	},
});

export const IFCFooter = ({
	onPickerClose,
	columnsDataId,
	columnInsertIndex,
	rightSideComponent,
}: Parameters<FooterType>[0]) => {
	const contactAdminGalaxia = expVal('project-terminology-refresh', 'isEnabled', false)
		? messages.contactAdminGalaxia
		: messages.contactAdmin;
	const { ifcConfig, addFieldToColumns } = useIFCConfigContext();
	const { formatMessage } = useIntl();
	const isSiteAdmin = useIsAdmin();
	const isProjectAdmin = useIsProjectAdmin();
	const isTMP = useIsSimplifiedProject();
	const isAdmin = isSiteAdmin || isProjectAdmin;
	const isCMPAdmin = isAdmin && !isTMP;
	const isTMPAdmin = isAdmin && isTMP;
	const { clearIndex } = useInlineCreateActiveIndexActions();
	const environment = useRelayEnvironment();
	const editField = useIFCEditField();

	const onStartFieldCreate = useCallback(
		({ name, type }: { name: string; type: string }) => {
			const field = { fieldSetId: TEMP_CREATE_FIELDSET_ID, name, type };
			createOptimisticFieldConfig({
				environment,
				fieldConfigId: columnsDataId,
				fields: [field],
				createIndex: columnInsertIndex,
			});
		},
		[columnInsertIndex, columnsDataId, environment],
	);

	const onFieldCreated = useCallback(
		(fieldId: string, fieldSetId?: string) => {
			if (fieldSetId) {
				updateTempOptimisticFieldSetId({
					environment,
					fieldConfigId: columnsDataId,
					fieldSetId,
				});

				addFieldToColumns?.(fieldSetId, columnInsertIndex, () => {
					deleteOptimisticFieldConfig({
						environment,
						fieldConfigId: columnsDataId,
						fields: [
							{
								fieldSetId,
								name: '',
							},
						],
					});
				});

				editField(fieldId, fieldSetId);
			}
		},
		[environment, columnsDataId, addFieldToColumns, columnInsertIndex, editField],
	);

	const onFieldCreateError = useCallback(() => {
		deleteOptimisticFieldConfig({
			environment,
			fieldConfigId: columnsDataId,
			fields: [
				{
					fieldSetId: TEMP_CREATE_FIELDSET_ID,
					name: '',
				},
			],
		});
	}, [columnsDataId, environment]);

	const { createAnalyticsEvent } = useAnalyticsEvents();

	const clickHandler = useCallback(
		(event: React.MouseEvent) => {
			onPickerClose?.(event);
			clearIndex(FIELD);
			ifcConfig?.onCreate({ onFieldCreated, onStartFieldCreate, onFieldCreateError });

			fireUIAnalytics(
				createAnalyticsEvent({
					action: 'clicked',
					actionSubject: 'button',
				}),
				'createFieldIFCFooterButton',
			);
		},
		[
			onPickerClose,
			clearIndex,
			ifcConfig,
			onFieldCreated,
			onStartFieldCreate,
			onFieldCreateError,
			createAnalyticsEvent,
		],
	);

	if (!isAdmin) {
		if (fg('slv-117-nin-column-picker-style-updates')) {
			if (rightSideComponent == null) {
				return null;
			}

			return (
				<Box xcss={styles.footerContainer}>
					<Flex justifyContent="end">{rightSideComponent}</Flex>
				</Box>
			);
		}

		return (
			<Box xcss={styles.ifcFooter}>
				<Inline space="space.100" alignBlock="center" alignInline="center">
					<InformationIcon label="" color={token('color.icon.accent.blue')} />
					<Text>
						{formatMessage(contactAdminGalaxia, {
							link: (chunks: ReactNode) => (
								<Anchor
									href="/secure/ContactAdministrators!default.jspa"
									target="_blank"
									onClick={() => {
										fireUIAnalytics(
											createAnalyticsEvent({
												action: 'clicked',
												actionSubject: 'link',
											}),
											'endUserIFCFooterLink',
										);
									}}
								>
									{chunks}
								</Anchor>
							),
						})}
					</Text>
				</Inline>
			</Box>
		);
	}

	if (
		isTMPAdmin &&
		ifcConfig &&
		(fg('jsc_list_category_editing_in_nin')
			? ifcConfig.isEnabledForProject
			: ifcConfig.IFCTriggerRef)
	) {
		if (fg('slv-117-nin-column-picker-style-updates')) {
			return (
				<Box xcss={styles.footerContainer}>
					<Inline grow="fill" spread="space-between" alignBlock="center">
						<ifcConfig.IFCTriggerRef>
							{({ triggerRef }) => (
								<Button
									ref={triggerRef}
									onClick={clickHandler}
									spacing="compact"
									appearance="subtle"
									iconBefore={AddIcon}
								>
									{formatMessage(messages.createAnotherButton)}
								</Button>
							)}
						</ifcConfig.IFCTriggerRef>
						{rightSideComponent}
					</Inline>
				</Box>
			);
		}

		return (
			<Box xcss={styles.ifcFooter}>
				<ifcConfig.IFCTriggerRef>
					{({ triggerRef }) => (
						<Button ref={triggerRef} onClick={clickHandler} shouldFitContainer>
							<Inline space="space.100" alignBlock="center" alignInline="center">
								<AddIcon label="" />
								<Text>{formatMessage(messages.createAnotherButton)}</Text>
							</Inline>
						</Button>
					)}
				</ifcConfig.IFCTriggerRef>
			</Box>
		);
	}

	if (isCMPAdmin) {
		if (fg('slv-117-nin-column-picker-style-updates')) {
			return (
				<Box xcss={styles.footerContainer}>
					<Inline grow="fill" spread="space-between" alignBlock="center">
						<Anchor
							xcss={styles.anchor}
							href={
								expVal('jira-admin-pages-centralised-fields-m1-experiment', 'isEnabled', false)
									? '/jira/settings/issues/fields'
									: '/secure/admin/ViewCustomFields.jspa'
							}
							target="_blank"
							onClick={() => {
								fireUIAnalytics(
									createAnalyticsEvent({
										action: 'clicked',
										actionSubject: 'link',
									}),
									'cmpAdminIFCFooterLink',
								);
							}}
						>
							{formatMessage(messages.createAFieldInConfig)}
						</Anchor>
						{rightSideComponent}
					</Inline>
				</Box>
			);
		}

		return (
			<Box xcss={styles.ifcFooter}>
				<Inline space="space.100" alignBlock="center" alignInline="center">
					<InformationIcon label="" color={token('color.icon.accent.blue')} />
					<Text>
						{formatMessage(messages.makeChangesInConfig, {
							link: (chunks: ReactNode) => (
								<Anchor
									href={
										expVal('jira-admin-pages-centralised-fields-m1-experiment', 'isEnabled', false)
											? '/jira/settings/issues/fields'
											: '/secure/admin/ViewCustomFields.jspa'
									}
									target="_blank"
									onClick={() => {
										fireUIAnalytics(
											createAnalyticsEvent({
												action: 'clicked',
												actionSubject: 'link',
											}),
											'cmpAdminIFCFooterLink',
										);
									}}
								>
									{chunks}
								</Anchor>
							),
						})}
					</Text>
				</Inline>
			</Box>
		);
	}
};
